import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Link2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface TaskDependenciesProps {
  taskId: string;
  userId: string;
}

export function TaskDependencies({ taskId, userId }: TaskDependenciesProps) {
  const { toast } = useToast();
  const [dependencies, setDependencies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [availableTasks, setAvailableTasks] = useState<any[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [dependencyType, setDependencyType] = useState<'blocks' | 'blocked_by' | 'related_to'>('blocks');
  const [hasBlockingDeps, setHasBlockingDeps] = useState(false);

  useEffect(() => {
    loadDependencies();
    checkBlockingDependencies();
  }, [taskId]);

  const loadDependencies = async () => {
    try {
      const { data, error } = await supabase
        .from('task_dependencies')
        .select(`
          id,
          dependency_type,
          depends_on_task:custom_tasks!depends_on_task_id(
            id,
            task_title,
            status,
            category_type
          )
        `)
        .eq('task_id', taskId);

      if (error) throw error;
      setDependencies(data || []);
    } catch (error) {
      console.error('Error loading dependencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkBlockingDependencies = async () => {
    try {
      const { data } = await supabase.rpc('has_blocking_dependencies', { p_task_id: taskId });
      setHasBlockingDeps(data || false);
    } catch (error) {
      console.error('Error checking blocking dependencies:', error);
    }
  };

  const loadAvailableTasks = async (query: string = '') => {
    try {
      let queryBuilder = supabase
        .from('custom_tasks')
        .select('id, task_title, status, category_type, una_id')
        .eq('una_id', (await supabase.from('custom_tasks').select('una_id').eq('id', taskId).single()).data?.una_id || '')
        .neq('id', taskId)
        .neq('status', 'completed')
        .order('task_title', { ascending: true })
        .limit(50);

      if (query.trim()) {
        queryBuilder = queryBuilder.ilike('task_title', `%${query}%`);
      }

      const { data, error } = await queryBuilder;
      if (error) throw error;
      setAvailableTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      loadAvailableTasks(searchQuery);
    }
  }, [dialogOpen, searchQuery]);

  const handleAdd = async () => {
    if (!selectedTaskId) {
      toast({
        title: "Error",
        description: "Please select a task",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('task_dependencies')
        .insert({
          task_id: taskId,
          depends_on_task_id: selectedTaskId,
          dependency_type: dependencyType,
          created_by: userId,
        });

      if (error) throw error;

      toast({
        title: "Dependency Added",
        description: "Task dependency created successfully",
      });

      setDialogOpen(false);
      setSelectedTaskId("");
      await loadDependencies();
      await checkBlockingDependencies();
    } catch (error: any) {
      console.error('Error adding dependency:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add dependency",
        variant: "destructive",
      });
    }
  };

  const handleRemove = async (dependencyId: string) => {
    try {
      const { error } = await supabase
        .from('task_dependencies')
        .delete()
        .eq('id', dependencyId);

      if (error) throw error;

      toast({
        title: "Dependency Removed",
        description: "Task dependency removed successfully",
      });

      await loadDependencies();
      await checkBlockingDependencies();
    } catch (error) {
      console.error('Error removing dependency:', error);
      toast({
        title: "Error",
        description: "Failed to remove dependency",
        variant: "destructive",
      });
    }
  };

  const getTypeLabel = (type: string) => {
    const labels = {
      blocks: 'Blocks',
      blocked_by: 'Blocked By',
      related_to: 'Related To',
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getTypeColor = (type: string) => {
    const colors = {
      blocks: 'border-orange-500 bg-orange-50 dark:bg-orange-950',
      blocked_by: 'border-red-500 bg-red-50 dark:bg-red-950',
      related_to: 'border-blue-500 bg-blue-50 dark:bg-blue-950',
    };
    return colors[type as keyof typeof colors] || '';
  };

  if (loading) return <div className="text-center py-4">Loading dependencies...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Dependencies ({dependencies.length})
        </h3>
        <Button onClick={() => setDialogOpen(true)} size="sm" variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      {hasBlockingDeps && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This task has incomplete blocking dependencies. Complete them first before marking this task as done.
          </AlertDescription>
        </Alert>
      )}

      {dependencies.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          <p>No dependencies yet. Link related tasks to manage workflows.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {dependencies.map((dep) => (
            <Card key={dep.id} className={`p-3 ${getTypeColor(dep.dependency_type)}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {getTypeLabel(dep.dependency_type)}
                    </Badge>
                    <Badge variant={dep.depends_on_task.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                      {dep.depends_on_task.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="font-medium">{dep.depends_on_task.task_title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {dep.depends_on_task.category_type}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => handleRemove(dep.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Task Dependency</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Dependency Type</Label>
              <Select value={dependencyType} onValueChange={(val: any) => setDependencyType(val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocks">This task blocks</SelectItem>
                  <SelectItem value="blocked_by">This task is blocked by</SelectItem>
                  <SelectItem value="related_to">Related to</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Search Tasks</Label>
              <Input
                placeholder="Type to search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div>
              <Label>Select Task</Label>
              <div className="border rounded-md max-h-[300px] overflow-y-auto">
                {availableTasks.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground">
                    {searchQuery ? 'No tasks found' : 'Start typing to filter tasks'}
                  </div>
                ) : (
                  availableTasks.map(task => (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => setSelectedTaskId(task.id)}
                      className={`w-full text-left p-2 border-b last:border-b-0 hover:bg-muted ${selectedTaskId === task.id ? 'bg-muted' : ''}`}
                    >
                      <div className="font-medium text-sm">{task.task_title}</div>
                      <div className="text-xs text-muted-foreground">{task.status.replace('_', ' ')}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd}>
              Add Dependency
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
